<template>
  <div>
    <!-- Filters -->
    <b-row class="mb-3">
      <b-col md="3">
        <b-form-select v-model="statusFilter" :options="statusOptions" />
      </b-col>
      <b-col md="3">
        <b-input
          v-model="serviceFilter"
          :placeholder="$t('partner-prices.service-name-placeholder')"
          type="search"
        />
      </b-col>
      <b-col md="3">
        <b-input
          v-model="dateFrom"
          type="date"
          :placeholder="$t('partner-prices.date-from')"
        />
      </b-col>
      <b-col md="3">
        <b-input
          v-model="dateTo"
          type="date"
          :placeholder="$t('partner-prices.date-to')"
        />
      </b-col>
    </b-row>

    <b-table
      :items="filteredRequests"
      :fields="fields"
      :busy="loading"
      sort-by="updatedAt"
      sort-desc
      show-empty
      :empty-text="$t('partner-prices.no-requests')"
    >
      <template #table-busy>
        <div class="text-center my-3"><b-spinner /></div>
      </template>
      <template #cell(status)="{ value }">
        <b-badge :variant="statusVariant(value)">
          {{ statusLabel(value) }}
        </b-badge>
      </template>
      <template #cell(pricePurchasing)="{ value }">
        <span v-if="value != null">{{ value | currency }}</span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(requestedPrice)="{ value }">
        <span v-if="value != null">{{ value | currency }}</span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(updatedAt)="{ value }">
        {{ new Date(value).toLocaleDateString() }}
      </template>
      <template #cell(actions)="{ item }">
        <b-button
          v-if="item.status === 'pending_increase'"
          size="sm"
          variant="success"
          class="mr-1"
          :disabled="item._saving"
          @click="accept(item)"
        >
          {{ $t('partner-prices.accept') }}
        </b-button>
        <b-button
          v-if="item.status === 'pending_increase'"
          size="sm"
          variant="danger"
          :disabled="item._saving"
          @click="decline(item)"
        >
          {{ $t('partner-prices.decline') }}
        </b-button>
      </template>
    </b-table>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    partner: { type: Object, required: true },
  },
  data() {
    return {
      requests: [],
      loading: false,
      statusFilter: null,
      serviceFilter: '',
      dateFrom: null,
      dateTo: null,
    };
  },
  computed: {
    fields() {
      return [
        {
          key: 'service.name',
          label: this.$t('partner-prices.service'),
          sortable: true,
        },
        {
          key: 'pricePurchasing',
          label: this.$t('partner-prices.current-price'),
          class: 'text-right',
        },
        {
          key: 'requestedPrice',
          label: this.$t('partner-prices.requested-price'),
          class: 'text-right',
        },
        {
          key: 'status',
          label: this.$t('partner-prices.status'),
          sortable: true,
        },
        {
          key: 'updatedAt',
          label: this.$t('partner-prices.date'),
          sortable: true,
        },
        { key: 'actions', label: '' },
      ];
    },
    statusOptions() {
      return [
        { value: null, text: this.$t('partner-prices.all-statuses') },
        {
          value: 'pending_increase',
          text: this.$t('partner-prices.status-pending'),
        },
        { value: 'declined', text: this.$t('partner-prices.status-declined') },
      ];
    },
    filteredRequests() {
      return this.requests.filter((r) => {
        if (this.statusFilter && r.status !== this.statusFilter) return false;
        if (
          this.serviceFilter &&
          !r.service?.name
            ?.toLowerCase()
            .includes(this.serviceFilter.toLowerCase())
        )
          return false;
        if (this.dateFrom && new Date(r.updatedAt) < new Date(this.dateFrom))
          return false;
        if (
          this.dateTo &&
          new Date(r.updatedAt) > new Date(this.dateTo + 'T23:59:59')
        )
          return false;
        return true;
      });
    },
  },
  async created() {
    await this.load();
  },
  methods: {
    statusVariant(status) {
      return (
        { pending_increase: 'warning', declined: 'danger', active: 'success' }[
          status
        ] ?? 'secondary'
      );
    },
    statusLabel(status) {
      return (
        {
          pending_increase: this.$t('partner-prices.status-pending'),
          declined: this.$t('partner-prices.status-declined'),
          active: this.$t('partner-prices.status-active'),
        }[status] ?? status
      );
    },
    async load() {
      const partnerId = this.partner?.account?.id;
      if (!partnerId) return;
      this.loading = true;
      try {
        const { data } = await axios.get('/data-api/account-service', {
          params: {
            join: 'service',
            'filter[account.id][$eq]': partnerId,
          },
        });
        this.requests = (data?.data ?? data ?? []).filter(
          (r) => r.status && r.status !== 'active',
        );
      } finally {
        this.loading = false;
      }
    },
    async accept(item) {
      this.$set(item, '_saving', true);
      try {
        const { data } = await axios.post(
          `/data-api/account-service/${item.id}/accept-request`,
        );
        const idx = this.requests.indexOf(item);
        if (idx !== -1) this.requests.splice(idx, 1);
        this.$bvToast.toast(
          this.$t('partner-prices.accept-success', {
            service: item.service?.name,
          }),
          {
            title: this.$t('partner-prices.accepted'),
            variant: 'success',
            solid: true,
          },
        );
      } catch {
        this.$bvToast.toast(this.$t('partner-prices.accept-error'), {
          title: this.$t('partner-prices.error'),
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.$set(item, '_saving', false);
      }
    },
    async decline(item) {
      this.$set(item, '_saving', true);
      try {
        const { data } = await axios.post(
          `/data-api/account-service/${item.id}/decline-request`,
        );
        item.requestedPrice = data.requestedPrice;
        item.status = data.status;
        this.$bvToast.toast(
          this.$t('partner-prices.decline-success', {
            service: item.service?.name,
          }),
          {
            title: this.$t('partner-prices.declined'),
            variant: 'warning',
            solid: true,
          },
        );
      } catch {
        this.$bvToast.toast(this.$t('partner-prices.decline-error'), {
          title: this.$t('partner-prices.error'),
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.$set(item, '_saving', false);
      }
    },
  },
};
</script>
